/**
 * RestaurantSearch Component
 * Search and display restaurants near a location
 *
 * Thin orchestrator (behavior-preserving refactor, issue #240). All stateful and
 * side-effecting behavior lives in `useRestaurantSearch`; all rendering lives in
 * the `restaurantSearch/presentation/` components. This container only wires the
 * hook to the presentation tree, preserving the source render structure exactly.
 *
 * Rules of Hooks: `useRestaurantSearch` is called UNCONDITIONALLY at the top —
 * before the `OPENTABLE_ENABLED` feature-gate early return — exactly as the
 * source ran every hook before its early return.
 */

import React from 'react';
import { isFeatureEnabled } from '../config/features';
import { useRestaurantSearch } from './restaurantSearch/hooks/useRestaurantSearch';
import { FindRestaurantsButton } from './restaurantSearch/presentation/FindRestaurantsButton';
import { LocationDialog } from './restaurantSearch/presentation/LocationDialog';
import { LocationSelector } from './restaurantSearch/presentation/LocationSelector';
import { SearchHeader } from './restaurantSearch/presentation/SearchHeader';
import { SearchResults } from './restaurantSearch/presentation/SearchResults';

const RestaurantSearch = ({ location, trip, userProfile, onReservationCreated, initialFilters, initialSearchLocation }) => {
  const search = useRestaurantSearch(location, trip, initialFilters, initialSearchLocation);

  if (!isFeatureEnabled('OPENTABLE_ENABLED')) {
    return null;
  }

  const {
    restaurants,
    loading,
    error,
    showFilters,
    setShowFilters,
    searchLocation,
    setSearchLocation,
    selectedHotel,
    hasSearched,
    showLocationDialog,
    setShowLocationDialog,
    isCollapsed,
    setIsCollapsed,
    filters,
    hasActiveFilters,
    hasHotel,
    handleFilterChange,
    clearFilters,
    handleLocationSelect,
    handleFindRestaurantsClick,
    handleClearSearch
  } = search;

  return (
    <div style={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}>
      {!hasSearched && (
        <FindRestaurantsButton onClick={handleFindRestaurantsClick} />
      )}

      {showLocationDialog && (
        <LocationDialog
          location={location}
          hasHotel={hasHotel}
          selectedHotel={selectedHotel}
          onSelect={handleLocationSelect}
          onClose={() => setShowLocationDialog(false)}
        />
      )}

      {hasSearched && hasHotel && (
        <LocationSelector
          searchLocation={searchLocation}
          setSearchLocation={setSearchLocation}
          location={location}
          selectedHotel={selectedHotel}
        />
      )}

      {hasSearched && (
        <SearchHeader
          searchLocation={searchLocation}
          selectedHotel={selectedHotel}
          location={location}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          hasActiveFilters={hasActiveFilters}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          onClearSearch={handleClearSearch}
        />
      )}

      <SearchResults
        hasSearched={hasSearched}
        loading={loading}
        error={error}
        restaurants={restaurants}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        trip={trip}
        userProfile={userProfile}
        onReservationCreated={onReservationCreated}
      />

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default RestaurantSearch;
